/*@flow*/
import type { Element } from 'react';
import React, { useState } from 'react';
import { Tooltip } from 'react-bootstrap';
import OverlayTriggerWrapper from 'comps/OverlayTriggerWrapper';
import cx from 'classnames';
import NonEmptyArray from 'lib/NonEmptyArray';
import type { LocalsT } from 'lib/proforma/types';

const TooltipTrigger = (props) => (
  <OverlayTriggerWrapper
    trigger={['hover']}
    placement={props.placement || 'left'}
    overlay={<Tooltip id={`${props.id}-tooltip`}>{props.text}</Tooltip>}
  >
    {props.children}
  </OverlayTriggerWrapper>
);

const ToggleBtn = (props) => {
  const hasValue = NonEmptyArray.is(props.value);
  if (!hasValue) return null;

  return props.isOpen ? (
    <a
      onClick={() => props.onClick()}
      style={{
        display: 'inline-block',
        marginLeft: 0,
      }}
      title="Collapse"
    >
      <i className="fa fa-compress" style={{ fontSize: '0.8em' }} />
    </a>
  ) : (
    <a
      onClick={() => props.onClick()}
      style={{ display: 'inline-block', marginLeft: 0 }}
      title="Expand"
    >
      <i className="fa fa-expand" style={{ fontSize: '0.8em' }} />
    </a>
  );
};

export default ({
  hasError,
  help,
  error,
  items = [],
  onChange,
  value,
}: LocalsT): Element<'div'> => {
  const [isOpen, setOpen] = useState(false);
  const showAddBtn = isOpen || !NonEmptyArray.is(value);

  const updateFormatter = (key: string, v: any, i: number) => {
    onChange(
      value.slice().map((v2, j) => (i === j ? { ...v2, [key]: v } : v2))
    );
  };
  const removeFormatter = (i) => onChange(value.filter((_, j) => i !== j));

  return (
    <div
      style={{ marginTop: 10, marginBottom: 0 }}
      className={cx('form-group', { 'has-error': hasError })}
    >
      {error}
      <div className="hide">{items.map((v) => (v: any).input)}</div>
      <div style={{ borderLeft: 'dashed 2px #ccc', paddingLeft: 7 }}>
        <label
          className="col-form-label position-relative"
          style={{
            border: 'none',
            marginTop: 0,
            paddingTop: 0,
            display: 'block',
            fontSize: 18,
          }}
        >
          Formatters ({value.length}){' '}
          <ToggleBtn
            value={value}
            isOpen={isOpen}
            onClick={() => setOpen((v) => !v)}
          />
        </label>
        {isOpen && (
          <div className="items">
            {value.map((v, i) => {
              const item: any = items[i];
              if (!item) return null;
              const btns = item.buttons;

              return (
                <div
                  key={i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 80px',
                    gridColumnGap: 5,
                  }}
                >
                  <div style={{ marginBottom: 10 }}>
                    <input
                      spellCheck={false}
                      style={{
                        width: '100%',
                        border: 'none',
                        borderBottom: 'solid 1px #ccc',
                      }}
                      type="text"
                      placeholder="Field name"
                      value={v.name}
                      onChange={(el) => {
                        updateFormatter('name', el.target.value, i);
                      }}
                    />
                  </div>
                  <div>
                    <input
                      spellCheck={false}
                      style={{
                        width: '100%',
                        border: 'none',
                        borderBottom: 'solid 1px #ccc',
                      }}
                      type="text"
                      placeholder="Label"
                      value={v.labelTemplate || ''}
                      onChange={(el) => {
                        updateFormatter('labelTemplate', el.target.value, i);
                      }}
                    />
                  </div>
                  <div>
                    <input
                      spellCheck={false}
                      style={{
                        width: '100%',
                        border: 'none',
                        borderBottom: 'solid 1px #ccc',
                      }}
                      type="text"
                      placeholder="Value"
                      value={v.valueTemplate || ''}
                      onChange={(el) => {
                        updateFormatter('valueTemplate', el.target.value, i);
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gridColumnGap: 1,
                      textAlign: 'center',
                      alignItems: 'center',
                    }}
                    className={`row-${i}`}
                  >
                    <TooltipTrigger id={`remove-row-${i}`} text="Remove">
                      <a onClick={() => removeFormatter(i)}>
                        <i className="fa fa-times" />
                      </a>
                    </TooltipTrigger>
                    <TooltipTrigger id={`move-row-up-${i}`} text="Move up">
                      <a
                        onClick={() => {
                          const moveUpBtn = btns.find(
                            (v2) => v2.type === 'move-up'
                          );
                          moveUpBtn && moveUpBtn.click();
                        }}
                        disabled={i === 0}
                      >
                        <i className="fa fa-arrow-up" />
                      </a>
                    </TooltipTrigger>
                    <TooltipTrigger id={`move-row-down-${i}`} text="Move down">
                      <a
                        onClick={() => {
                          const moveDownBtn = btns.find(
                            (v2) => v2.type === 'move-down'
                          );
                          moveDownBtn.click();
                        }}
                      >
                        <i className="fa fa-arrow-down" />
                      </a>
                    </TooltipTrigger>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {showAddBtn ? (
          <a
            className="btn btn-secondary"
            style={{ margin: 0 }}
            onClick={() => {
              setOpen(true);
              onChange([
                ...value,
                {
                  name: '',
                  valueTemplate: '{{value}}',
                  labelTemplate: '{{key}}',
                },
              ]);
            }}
          >
            ADD
          </a>
        ) : null}
      </div>
      {help}
    </div>
  );
};
